import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './styles.css';

/**
 * NestedCampaignWarningModal (Shared)
 * 
 * Shows when user tries to manually change the status of a task with a nested campaign
 * that has auto-sync enabled. Warns them that this will disable automatic status updates.
 * 
 * Features:
 * - Explains auto-sync will be disabled
 * - Option to proceed with manual override
 * - Option to cancel and keep auto-sync
 * 
 * @param {boolean} isOpen - Whether the modal is visible
 * @param {number} nestedSprintProgress - Current progress percentage of nested campaign
 * @param {string} targetStatusName - The status the user is trying to change to
 * @param {function} onConfirm - Callback when user confirms they want to override
 * @param {function} onCancel - Callback when user cancels
 */
const NestedCampaignWarningModal = ({
  isOpen,
  nestedSprintProgress = 0,
  targetStatusName = '',
  onConfirm,
  onCancel,
}) => {
  const cancelBtnRef = useRef(null);

  useEffect(() => {
    if (isOpen && cancelBtnRef.current) {
      cancelBtnRef.current.focus();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onCancel?.();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onCancel?.();
    }
  };

  const modal = (
    <div 
      className="nested-warning-modal__backdrop" 
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      <div className="nested-warning-modal__container">
        {/* Friendly emoji */}
        <div className="nested-warning-modal__emoji">✨</div>

        {/* Conversational content */}
        <div className="nested-warning-modal__content">
          <h3 className="nested-warning-modal__title">
            Heads up!
          </h3>
          
          <p className="nested-warning-modal__message">
            This mission has a campaign inside it ({nestedSprintProgress}% done), and right now its status 
            <strong> updates automatically</strong> as you complete things inside.
          </p>
          
          <p className="nested-warning-modal__message nested-warning-modal__message--secondary">
            Want to set the status yourself instead? You can always turn auto-updates back on later.
          </p>
        </div>

        {/* Actions */}
        <div className="nested-warning-modal__footer">
          <button 
            ref={cancelBtnRef}
            className="nested-warning-modal__btn nested-warning-modal__btn--primary"
            onClick={onCancel}
          >
            Keep it automatic ✨
          </button>
          
          <button 
            className="nested-warning-modal__btn nested-warning-modal__btn--secondary"
            onClick={onConfirm}
          >
            I'll set it to "{targetStatusName}"
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default NestedCampaignWarningModal;
